import React, { useState, useEffect } from "react";
import { useGet, usePost } from "../../api";
import { IMerchantSimple, IUpsertMerchant } from "../../interfaces";
import {
  Table,
  Form,
  Button,
  ButtonToolbar,
  Alert,
  Spinner,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { Link } from "react-router";
import { useApi } from "../../hooks";

interface EditableMerchant extends IMerchantSimple {
  isEditing?: boolean;
  hasChanges?: boolean;
}

export const EditAllMerchants: React.FC = () => {
  const [merchants, setMerchants] = useState<EditableMerchant[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string>("");

  const { data: merchantsData } = useGet<IMerchantSimple[]>(
    `/merchants?page=1&limit=100`
  );
  const updateMerchant = usePost<string, Partial<IUpsertMerchant>>();
  const { get } = useApi();
  const [running, setRunning] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (merchantsData) {
      setMerchants(
        merchantsData.map((m) => ({
          ...m,
          isEditing: false,
          hasChanges: false,
        }))
      );
      setLoading(false);
    }
  }, [merchantsData]);

  const handleEdit = (merchantId: string) => {
    setMerchants((prev) =>
      prev.map((m) => (m._id === merchantId ? { ...m, isEditing: true } : m))
    );
  };

  const handleCancel = (merchantId: string) => {
    const originalMerchant = merchantsData?.find((m) => m._id === merchantId);
    if (originalMerchant) {
      setMerchants((prev) =>
        prev.map((m) =>
          m._id === merchantId
            ? { ...originalMerchant, isEditing: false, hasChanges: false }
            : m
        )
      );
    }
    setErrors((prev) => ({ ...prev, [merchantId]: "" }));
  };

  const handleFieldChange = (
    merchantId: string,
    field: keyof IMerchantSimple,
    value: any
  ) => {
    setMerchants((prev) =>
      prev.map((m) =>
        m._id === merchantId ? { ...m, [field]: value, hasChanges: true } : m
      )
    );
    setErrors((prev) => ({ ...prev, [merchantId]: "" }));
  };

  const handleSave = async (merchantId: string) => {
    const merchant = merchants.find((m) => m._id === merchantId);
    if (!merchant) return;

    // Basic validation
    if (!merchant.name?.trim()) {
      setErrors((prev) => ({ ...prev, [merchantId]: "Name is required" }));
      return;
    }
    if (!merchant.homePageUrl?.trim()) {
      setErrors((prev) => ({
        ...prev,
        [merchantId]: "Home Page URL is required",
      }));
      return;
    }
    if (!merchant.countryCode?.trim()) {
      setErrors((prev) => ({
        ...prev,
        [merchantId]: "Country Code is required",
      }));
      return;
    }

    setSaving((prev) => ({ ...prev, [merchantId]: true }));
    setErrors((prev) => ({ ...prev, [merchantId]: "" }));

    try {
      const updateData: Partial<IUpsertMerchant> = {
        name: merchant.name,
        countryCode: merchant.countryCode,
        homePageUrl: merchant.homePageUrl,
        minimumCartValue: merchant.minimumCartValue,
        recommendedTestProducts: merchant.recommendedTestProducts,
        additionalSettings: merchant.additionalSettings,
        cachedProductPages: merchant.cachedProductPages,
      };

      await updateMerchant.execute(`/merchants/${merchantId}`, updateData);

      setMerchants((prev) =>
        prev.map((m) =>
          m._id === merchantId
            ? { ...m, isEditing: false, hasChanges: false }
            : m
        )
      );

      setSuccessMessage(`Successfully updated ${merchant.name}`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        [merchantId]:
          error instanceof Error ? error.message : "Failed to update merchant",
      }));
    } finally {
      setSaving((prev) => ({ ...prev, [merchantId]: false }));
    }
  };

  const handleSaveAll = async () => {
    const changedMerchants = merchants.filter((m) => m.hasChanges);

    for (const merchant of changedMerchants) {
      await handleSave(merchant._id);
    }
  };

  // Enqueue a journey test for this merchant right now, bypassing the
  // scheduler's staleness/dedup throttle. The runner worker picks it up on its
  // next claim; results land in Template Results.
  const handleRunNow = async (merchant: EditableMerchant) => {
    const productUrl = merchant.cachedProductPages?.[0]?.url;
    if (!productUrl) {
      setErrors((prev) => ({
        ...prev,
        [merchant._id]: "Set a Test Product URL before running a journey test.",
      }));
      return;
    }
    setRunning((prev) => ({ ...prev, [merchant._id]: true }));
    setErrors((prev) => ({ ...prev, [merchant._id]: "" }));
    try {
      // Unified merchant-map run (same as the merchants list "Build / Test Map"):
      // builds the map if missing, refreshes + verifies it if present, mobile-first.
      await get(
        `/test/browser?merchantId=${merchant._id}&runType=capture`
      );
      setSuccessMessage(
        `Started a mobile map build/test for ${merchant.name}. Watch it under Tasks; the result lands in Template Results.`
      );
    } catch {
      setErrors((prev) => ({
        ...prev,
        [merchant._id]: "Failed to start map build/test.",
      }));
    } finally {
      setRunning((prev) => ({ ...prev, [merchant._id]: false }));
    }
  };

  const EditableCell: React.FC<{
    value: string | number | undefined;
    type?: "text" | "url" | "number";
    isEditing: boolean;
    onChange: (value: string) => void;
    placeholder?: string;
    maxLength?: number;
  }> = ({
    value,
    type = "text",
    isEditing,
    onChange,
    placeholder,
    maxLength,
  }) => {
    if (!isEditing) {
      return (
        <span className="d-block text-truncate" style={{ maxWidth: "200px" }}>
          {value || "-"}
        </span>
      );
    }

    return (
      <Form.Control
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        size="sm"
        maxLength={maxLength}
        style={{ minWidth: "150px" }}
      />
    );
  };

  const BooleanCell: React.FC<{
    value: boolean | undefined;
    isEditing: boolean;
    onChange: (value: boolean) => void;
  }> = ({ value, isEditing, onChange }) => {
    if (!isEditing) {
      return (
        <Badge bg={value ? "success" : "secondary"}>
          {value ? "Yes" : "No"}
        </Badge>
      );
    }

    return (
      <Form.Check
        type="checkbox"
        checked={value || false}
        onChange={(e) => onChange(e.target.checked)}
      />
    );
  };

  const ProductPageCell: React.FC<{
    value:
      | Array<{
          url: string;
          skipSkuCheck: boolean;
          validationSuccessCount: number;
          validationFailureCount: number;
        }>
      | undefined;
    isEditing: boolean;
    onChange: (
      value: Array<{
        url: string;
        skipSkuCheck: boolean;
        validationSuccessCount: number;
        validationFailureCount: number;
      }>
    ) => void;
    placeholder?: string;
  }> = ({ value, isEditing, onChange, placeholder }) => {
    const firstPage = value?.[0];
    const displayValue = firstPage?.url || "";

    if (!isEditing) {
      return (
        <span className="d-block text-truncate" style={{ maxWidth: "200px" }}>
          {displayValue ? (
            displayValue.startsWith("http") ? (
              <a
                href={displayValue}
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-none"
                title={displayValue}
              >
                {displayValue.length > 40
                  ? `${displayValue.substring(0, 40)}...`
                  : displayValue}
              </a>
            ) : (
              displayValue
            )
          ) : (
            "-"
          )}
          {value && value.length > 1 && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{value.map((p) => p.url).join(", ")}</Tooltip>}
            >
              <Badge bg="info" className="ms-1">
                +{value.length - 1}
              </Badge>
            </OverlayTrigger>
          )}
        </span>
      );
    }

    return (
      <Form.Control
        type="url"
        value={displayValue}
        onChange={(e) => {
          const newUrl = e.target.value.trim();
          if (newUrl) {
            const newPage = {
              url: newUrl,
              skipSkuCheck: firstPage?.skipSkuCheck || false,
              validationSuccessCount: firstPage?.validationSuccessCount || 0,
              validationFailureCount: firstPage?.validationFailureCount || 0,
            };
            onChange([newPage]);
          } else {
            onChange([]);
          }
        }}
        placeholder={placeholder}
        size="sm"
        style={{ minWidth: "200px" }}
      />
    );
  };

  const ArrayCell: React.FC<{
    value: string[] | undefined;
    isEditing: boolean;
    onChange: (value: string[]) => void;
    placeholder?: string;
    type?: "text" | "url";
  }> = ({ value, isEditing, onChange, placeholder, type = "text" }) => {
    const displayValue = value?.[0] || "";

    if (!isEditing) {
      return (
        <span className="d-block text-truncate" style={{ maxWidth: "150px" }}>
          {displayValue ? (
            type === "url" && displayValue.startsWith("http") ? (
              <a
                href={displayValue}
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-none"
                title={displayValue}
              >
                {displayValue.length > 30
                  ? `${displayValue.substring(0, 30)}...`
                  : displayValue}
              </a>
            ) : (
              displayValue
            )
          ) : (
            "-"
          )}
          {value && value.length > 1 && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{value.join(", ")}</Tooltip>}
            >
              <Badge bg="info" className="ms-1">
                +{value.length - 1}
              </Badge>
            </OverlayTrigger>
          )}
        </span>
      );
    }

    return (
      <Form.Control
        type={type}
        value={displayValue}
        onChange={(e) => {
          const newValue = e.target.value.trim();
          onChange(newValue ? [newValue] : []);
        }}
        placeholder={placeholder}
        size="sm"
        style={{ minWidth: "150px" }}
      />
    );
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  const hasAnyChanges = merchants.some((m) => m.hasChanges);

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Edit All Merchants</h2>
        <ButtonToolbar>
          <Button
            as={Link as any}
            to="/merchants/new"
            variant="success"
            size="sm"
            className="me-2"
          >
            <i className="bi bi-plus-lg"></i> Add New
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSaveAll}
            disabled={!hasAnyChanges}
          >
            <i className="bi bi-floppy"></i> Save All Changes
          </Button>
        </ButtonToolbar>
      </div>

      {successMessage && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}

      <div className="table-responsive">
        <Table striped bordered hover size="sm" style={{ fontSize: "0.9rem" }}>
          <thead className="table-dark">
            <tr>
              <th style={{ width: "50px" }}>Actions</th>
              <th style={{ width: "150px" }}>Brand</th>
              <th style={{ width: "200px" }}>Home Page URL</th>
              <th style={{ width: "80px" }}>Country</th>
              <th style={{ width: "150px" }}>Cart Page</th>
              <th style={{ width: "150px" }}>Product Search</th>
              <th style={{ width: "150px" }}>Recommended Products</th>
              <th style={{ width: "200px" }}>Test Product URL</th>
              <th style={{ width: "100px" }}>Journey Test</th>
              <th style={{ width: "100px" }}>Skip SKU</th>
              <th style={{ width: "100px" }}>Cookie Accept</th>
              <th style={{ width: "100px" }}>Newsletter</th>
              <th style={{ width: "100px" }}>Regional Popup</th>
              <th style={{ width: "100px" }}>Min Cart Value</th>
            </tr>
          </thead>
          <tbody>
            {merchants.map((merchant) => (
              <tr
                key={merchant._id}
                className={merchant.hasChanges ? "table-warning" : ""}
              >
                <td>
                  <div className="d-flex gap-1">
                    {merchant.isEditing ? (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleSave(merchant._id)}
                          disabled={saving[merchant._id]}
                        >
                          {saving[merchant._id] ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <i className="bi bi-check" />
                          )}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleCancel(merchant._id)}
                          disabled={saving[merchant._id]}
                        >
                          <i className="bi bi-x" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(merchant._id)}
                      >
                        <i className="bi bi-pencil" />
                      </Button>
                    )}
                    <Button
                      variant="outline-success"
                      size="sm"
                      title={
                        merchant.cachedProductPages?.[0]?.url
                          ? "Build / test the merchant map now (mobile)"
                          : "Set a Test Product URL first"
                      }
                      onClick={() => handleRunNow(merchant)}
                      disabled={
                        running[merchant._id] ||
                        !merchant.cachedProductPages?.[0]?.url
                      }
                    >
                      {running[merchant._id] ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <i className="bi bi-lightning-charge" />
                      )}
                    </Button>
                  </div>
                  {errors[merchant._id] && (
                    <div className="text-danger small mt-1">
                      {errors[merchant._id]}
                    </div>
                  )}
                </td>

                <td>
                  <EditableCell
                    value={merchant.name}
                    isEditing={merchant.isEditing || false}
                    onChange={(value) =>
                      handleFieldChange(merchant._id, "name", value)
                    }
                    placeholder="Brand name"
                    maxLength={100}
                  />
                </td>

                <td>
                  <EditableCell
                    value={merchant.homePageUrl}
                    type="url"
                    isEditing={merchant.isEditing || false}
                    onChange={(value) =>
                      handleFieldChange(merchant._id, "homePageUrl", value)
                    }
                    placeholder="https://example.com"
                  />
                </td>

                <td>
                  <EditableCell
                    value={merchant.countryCode}
                    isEditing={merchant.isEditing || false}
                    onChange={(value) =>
                      handleFieldChange(
                        merchant._id,
                        "countryCode",
                        value.toUpperCase()
                      )
                    }
                    placeholder="US"
                    maxLength={2}
                  />
                </td>

                <td>
                  <EditableCell
                    value={merchant.additionalSettings?.knownCartPage}
                    type="url"
                    isEditing={merchant.isEditing || false}
                    onChange={(value) =>
                      handleFieldChange(merchant._id, "additionalSettings", {
                        ...merchant.additionalSettings,
                        knownCartPage: value,
                      })
                    }
                    placeholder="https://example.com/cart"
                  />
                </td>

                <td>
                  <BooleanCell
                    value={merchant.additionalSettings?.hasSearchToggle}
                    isEditing={merchant.isEditing || false}
                    onChange={(value) =>
                      handleFieldChange(merchant._id, "additionalSettings", {
                        ...merchant.additionalSettings,
                        hasSearchToggle: value,
                      })
                    }
                  />
                </td>

                <td>
                  <ArrayCell
                    value={merchant.recommendedTestProducts}
                    isEditing={merchant.isEditing || false}
                    onChange={(value) =>
                      handleFieldChange(
                        merchant._id,
                        "recommendedTestProducts",
                        value
                      )
                    }
                    placeholder="Product name or keyword"
                    type="text"
                  />
                </td>

                <td>
                  <ProductPageCell
                    value={merchant.cachedProductPages}
                    isEditing={merchant.isEditing || false}
                    onChange={(value) =>
                      handleFieldChange(
                        merchant._id,
                        "cachedProductPages",
                        value
                      )
                    }
                    placeholder="https://example.com/product"
                  />
                </td>

                <td>
                  <BooleanCell
                    value={merchant.additionalSettings?.journeyTestEnabled}
                    isEditing={merchant.isEditing || false}
                    onChange={(value) =>
                      handleFieldChange(merchant._id, "additionalSettings", {
                        ...merchant.additionalSettings,
                        journeyTestEnabled: value,
                      })
                    }
                  />
                </td>

                <td>
                  <BooleanCell
                    value={merchant.cachedProductPages?.[0]?.skipSkuCheck}
                    isEditing={merchant.isEditing || false}
                    onChange={(value) => {
                      const currentPages = merchant.cachedProductPages || [];
                      const updatedPages =
                        currentPages.length > 0
                          ? currentPages.map((page, index) =>
                              index === 0
                                ? { ...page, skipSkuCheck: value }
                                : page
                            )
                          : [
                              {
                                url: "",
                                skipSkuCheck: value,
                                validationSuccessCount: 0,
                                validationFailureCount: 0,
                              },
                            ];
                      handleFieldChange(
                        merchant._id,
                        "cachedProductPages",
                        updatedPages
                      );
                    }}
                  />
                </td>

                <td>
                  <BooleanCell
                    value={merchant.additionalSettings?.cookieDismissalRequired}
                    isEditing={merchant.isEditing || false}
                    onChange={(value) =>
                      handleFieldChange(merchant._id, "additionalSettings", {
                        ...merchant.additionalSettings,
                        cookieDismissalRequired: value,
                      })
                    }
                  />
                </td>

                <td>
                  <BooleanCell
                    value={
                      merchant.additionalSettings?.newsletterDismissalRequired
                    }
                    isEditing={merchant.isEditing || false}
                    onChange={(value) =>
                      handleFieldChange(merchant._id, "additionalSettings", {
                        ...merchant.additionalSettings,
                        newsletterDismissalRequired: value,
                      })
                    }
                  />
                </td>

                <td>
                  <BooleanCell
                    value={
                      merchant.additionalSettings?.regionalDismissalRequired
                    }
                    isEditing={merchant.isEditing || false}
                    onChange={(value) =>
                      handleFieldChange(merchant._id, "additionalSettings", {
                        ...merchant.additionalSettings,
                        regionalDismissalRequired: value,
                      })
                    }
                  />
                </td>

                <td>
                  <EditableCell
                    value={merchant.minimumCartValue?.toString()}
                    type="number"
                    isEditing={merchant.isEditing || false}
                    onChange={(value) =>
                      handleFieldChange(
                        merchant._id,
                        "minimumCartValue",
                        value ? parseFloat(value) : undefined
                      )
                    }
                    placeholder="0.00"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {merchants.length === 0 && (
        <div className="text-center py-5">
          <p className="text-muted">
            No merchants found.{" "}
            <Link to="/merchants/new">Add your first merchant</Link>
          </p>
        </div>
      )}

      <div className="mt-3 small text-muted">
        <strong>Tips:</strong>
        <ul className="mb-0">
          <li>Click the pencil icon to edit a row</li>
          <li>Changed rows are highlighted in yellow</li>
          <li>
            For arrays (like products), only the first item is shown. Hover over
            the badge to see all items.
          </li>
          <li>Use "Save All Changes" to bulk save all modified merchants</li>
          <li>Country codes should be 2 letters (e.g., US, UK, CA)</li>
          <li>
            <strong>Recommended Products:</strong> Product names or search
            keywords for testing
          </li>
          <li>
            <strong>Test Product URL:</strong> Direct product page URL for
            automated testing (e.g., https://store.com/product/item)
          </li>
          <li>
            <strong>Skip SKU:</strong> Whether to skip variant/size selection
            for the test product
          </li>
        </ul>
      </div>
    </div>
  );
};
